import {
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import {
  Redirect,
  Route,
  type RouteComponentProps,
  Switch,
  useLocation,
} from "react-router-dom";
import { CompatRoute } from "react-router-dom-v5-compat";
import { LazyLoading } from "@b2bportal/core-ui";
import { useInitializeNavigate } from "@b2bportal/core-utilities";
import { logger, trackEvent } from "@hopper-b2b/api";
import { useExperiment, useTrackingProperties } from "@hopper-b2b/experiments";
import FlightsApp from "@hopper-b2b/flights";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useChatSupport } from "@hopper-b2b/self-serve";
import { getDarkModePreferred } from "@hopper-b2b/themes";
import TripsApp, { UberTripsList } from "@hopper-b2b/trips";
import {
  type FlightModuleFeatureFlag,
  type LaunchedApplicationProperties,
  SearchParams,
} from "@hopper-b2b/types";
import { EmptySlot, SlotProvider, type Slots } from "@hopper-b2b/ui";
import {
  FeatureFlagsContext,
  type FeatureFlagsContextProps,
  getLandingScreen,
  tenantFlagsEnabled,
  useDeviceTypes,
  useLaunchedApplicationEvent,
  useUberBridge,
} from "@hopper-b2b/utilities";
import Grid from "@material-ui/core/Grid";
import clsx from "clsx";

import { EXPERIMENTS, FEATURE_FLAGS, UserContext } from "../../App";
import ClientLogo from "../../assets/client/client-logo.svg";
import { flightCheckoutProps } from "../../modules/flight-checkout";
import { priceFreezePurchaseProps } from "../../modules/price-freeze-purchase";
import { colors } from "../../utils/colors";
import config from "../../utils/config";
import {
  apiConfig,
  PATH_FLIGHTS,
  PATH_FLIGHTS_SEARCH,
  PATH_TRIPS,
} from "../../utils/urlPaths";
import { FtuFlightShopBanner } from "../FtuExperiment";
import Maintenance from "../Maintenance/maintenance";
import { LandingPageType, MobileLandingPage } from "../MobileLandingPage";
import {
  CashbackPriceSummary,
  FlightCalendarDateLabel,
  FlightCard,
  FlightConfirmationNumbersSection,
  FlightFintechSelectionDecline,
  FlightFintechSelectionOption,
  FintechPurchase,
  FlightMobileFareDetailsTripPrice,
  FlightPriceDropProtectionBanner,
  FlightShopSummaryPanel,
  FlightShopTransitionLoader,
  PriceRangeTag,
  SelectedSeatCard,
  TravelerSelectRow,
  FintechLearnMoreModal,
  TripsIsRyanair,
  VirtualInterliningBanner,
  MissedConnectionDisclaimer,
  FlightDetailsViAlert,
  SelfTransferDisclaimer,
  FareDetailsSliceHeader,
} from "../Slots";
import { darkModeIcons, lightModeIcons } from "./sharedIcons";

import useStyles from "./styles";
import "./styles.scss";

const CoreTripsApp = lazy(() => import("../../pages/Trips"));
const UberFlightsRebookApp = lazy(() => import("../../pages/FlightsRebook"));

const slots: Slots = {
  "flight-shop-summary-panel": FlightShopSummaryPanel,
  "flight-calendar-date-label": FlightCalendarDateLabel,
  "price-range-tag": PriceRangeTag,
  "traveler-select-row": TravelerSelectRow,
  "selected-seat-card": SelectedSeatCard,
  "flight-shop-transition-loader": FlightShopTransitionLoader,
  "flight-confirmation-confirmation-numbers-container":
    FlightConfirmationNumbersSection,
  "flight-shop-mobile-ftu-banner": FtuFlightShopBanner,
  "flight-price-drop-protection-banner": FlightPriceDropProtectionBanner,
  "flights-fintech-selection-page-option": FlightFintechSelectionOption,
  "flight-fintech-selection-page-decline-all": FlightFintechSelectionDecline,
  "flights-fintech-purchase": FintechPurchase,
  "cash-back-ftu-discount": CashbackPriceSummary,
  "flight-mobile-fare-details-trip-price": FlightMobileFareDetailsTripPrice,
  "passenger-form-mobile-top-save-right-button": EmptySlot,
  "flight-shop-list-mobile-card": FlightCard,
  // "price-breakdown-discount-price-line": PriceBreakdownDiscountPriceLine,
  "fintech-learn-more-modal": FintechLearnMoreModal,
  // Virtual Interlining
  "vi-banner": VirtualInterliningBanner,
  "missed-connection-disclaimer": MissedConnectionDisclaimer,
  "flight-details-vi-alert": FlightDetailsViAlert,
  "self-transfer-disclaimer": SelfTransferDisclaimer,
  "fare-details-slice-header": FareDetailsSliceHeader,
};

const tripSlots: Slots = {
  // Trips
  "mytrips-list": UberTripsList,
  "mytrips-ryanair-check-in": TripsIsRyanair,
};

interface IBodyProps {
  theme: any;
  className?: string;
  isFirstSession: boolean;
  launchEventSent: boolean;
  setLaunchEventSent: (val: boolean) => void;
  handleDarkModeChange: (val: boolean) => void;
  isDarkModePreferred?: boolean;
}

const Body = (props: IBodyProps) => {
  const {
    theme,
    className,
    launchEventSent,
    setLaunchEventSent,
    handleDarkModeChange,
    isDarkModePreferred,
  } = props;
  const { generalSupportChat } = useChatSupport();
  const { matchesMobile } = useDeviceTypes();
  const { pathname } = useLocation();

  const { setHeader } = useUberBridge();
  const { t } = useI18nContext();
  const classes = useStyles();

  const { sessionInfo } = useContext(UserContext);

  const isInMaintenance = useExperiment(FEATURE_FLAGS.MAINTENANCE);

  // Fintech Flags
  const enablePriceWatch = useExperiment(FEATURE_FLAGS.AIR_PRICE_WATCH);
  const enablePriceFreeze = useExperiment(FEATURE_FLAGS.AIR_PRICE_FREEZE);
  const enableScheduleChange = useExperiment(FEATURE_FLAGS.AIR_DISRUPTION);
  const enableMissedConnection = useExperiment(
    FEATURE_FLAGS.AIR_MISSED_CONNECTION
  );
  const enableCfar = useExperiment(FEATURE_FLAGS.AIR_CFAR);
  const enableChfar = useExperiment(FEATURE_FLAGS.AIR_CHFAR);
  const enableVipSupport = useExperiment(FEATURE_FLAGS.VIP_SUPPORT);
  const enableDisruptionExerciseRebooking = useExperiment(
    FEATURE_FLAGS.AIR_DISRUPTION_EXERCISE_REBOOKING
  );
  const enableDisruptionRebookingContactSupport = useExperiment(
    FEATURE_FLAGS.DISRUPTION_REBOOKING_CONTACT_SUPPORT
  );
  const enablePricePrediction = useExperiment(FEATURE_FLAGS.PRICE_PREDICTION);
  const enablePriceDropProtection = useExperiment(
    FEATURE_FLAGS.PRICE_DROP_PROTECTION
  );

  // Features
  const enableAirExchange = useExperiment(FEATURE_FLAGS.AIR_EXCHANGE);
  const enableDarkMode = useExperiment(FEATURE_FLAGS.APP_DARKMODE);
  const enableAirShopV4 = useExperiment(FEATURE_FLAGS.AIR_SHOP_V4);
  const enableLoadingSteps = useExperiment(FEATURE_FLAGS.LOADING_STEPS);
  const enableHFv2 = useExperiment(FEATURE_FLAGS.HFv2);
  // TODO: Fix experiments to load first before hydrating UI before
  const enableIataLookup = tenantFlagsEnabled.FlightLocationIataLookup;
  const uberTripsCoreMigration = useExperiment(
    FEATURE_FLAGS.UBER_TRIPS_CORE_MIGRATION
  );

  // Experiments
  const hideAirItineraryReview = useExperiment(
    EXPERIMENTS.HIDE_AIR_ITINERARY_REVIEW
  );
  const hideFareDetails = useExperiment(EXPERIMENTS.HIDE_FARE_DETAILS);
  const enableFintechSelection = useExperiment(
    EXPERIMENTS.AIR_FINTECH_SELECTION
  );
  const hideSeats = useExperiment(EXPERIMENTS.HIDE_SEATS);
  const hideAirPriceFreezeHackerFare = useExperiment(
    EXPERIMENTS.HIDE_AIR_PRICE_FREEZE_HACKER_FARE
  );
  const enableFlightCardUiUpdate = useExperiment(
    EXPERIMENTS.FLIGHT_CARD_UI_UPDATE
  );
  const enableFintechPurchaseErrorState = useExperiment(
    EXPERIMENTS.FINTECH_PURCHASE_ERROR_STATE
  );
  const dedupeFlightsInList = useExperiment(EXPERIMENTS.DEDUPE_FLIGHTS_IN_LIST);
  const enableHorizontalFareDetailsCard = useExperiment(
    EXPERIMENTS.HORIZONTAL_FARE_DETAILS_CARD
  );

  const hideCfarHackerFare = useExperiment(FEATURE_FLAGS.AIR_CFAR_HACKER_FARE);
  const hideChfarHackerFare = useExperiment(
    FEATURE_FLAGS.AIR_CHFAR_HACKER_FARE
  );
  const hideDisruptionHackerFare = useExperiment(
    FEATURE_FLAGS.AIR_DISRUPTION_HACKER_FARE
  );

  const trackingProperties: LaunchedApplicationProperties =
    useTrackingProperties({
      landing_screen: getLandingScreen(),
      url: document.location.pathname,
    });

  const launchSent = useLaunchedApplicationEvent(
    launchEventSent,
    trackingProperties,
    trackEvent
  );

  useEffect(() => {
    if (launchSent) {
      setLaunchEventSent(true);
    }
  }, [launchSent, setLaunchEventSent]);

  useEffect(() => {
    if (isInMaintenance) {
      window.parent.postMessage(
        {
          type: "START_SESSION",
          payload: null,
        },
        import.meta.env["VITE_UBER_URL"]
      );
    }
  }, [isInMaintenance]);

  const getHeaderTitle = useMemo(() => {
    const params = new URLSearchParams(document.location.search);
    if (pathname?.includes(PATH_FLIGHTS_SEARCH)) {
      if (
        params.get(SearchParams.Origin) &&
        params.get(SearchParams.Destination)
      ) {
        return t("flightsPageTitles.selectDate");
      } else if (params.get(SearchParams.Origin)) {
        return t("flightsPageTitles.selectReturn");
      } else {
        return t("flightsPageTitles.selectOutbound");
      }
    }
    return;
  }, [pathname, t]);

  useEffect(() => {
    if (getHeaderTitle !== undefined) {
      setHeader({
        title: getHeaderTitle,
      });
    }
  }, [getHeaderTitle, setHeader]);

  const featureFlags: FeatureFlagsContextProps = useMemo(
    () => ({
      enableAirExchange,
      enableCfar,
      enableChfar,
      enableDarkMode,
      enableFintechSelection,
      enableMissedConnection,
      enablePriceFreeze,
      enablePriceWatch,
      enableScheduleChange,
      enableVipSupport,
      enableDisruptionExerciseRebooking,
      enableDisruptionRebookingContactSupport,
      enablePriceDropProtection,
      enablePricePrediction,
      hideAirItineraryReview,
      hideFareDetails,
      hideSeats,
      enableAirShopV4,
      enableLoadingSteps,
      enableIataLookup,
      enableHFv2,
      hideAirPriceFreezeHackerFare,
      hideCfarHackerFare,
      hideChfarHackerFare,
      hideDisruptionHackerFare,
      enableFlightCardUiUpdate,
      enableFintechPurchaseErrorState,
      dedupeFlightsInList,
      enableHorizontalFareDetailsCard,
      uberTripsCoreMigration,
    }),
    [
      enableAirExchange,
      enableCfar,
      enableChfar,
      enableDarkMode,
      enableFintechSelection,
      enableMissedConnection,
      enablePriceFreeze,
      enablePriceWatch,
      enableScheduleChange,
      enableVipSupport,
      enableDisruptionExerciseRebooking,
      enableDisruptionRebookingContactSupport,
      enablePriceDropProtection,
      enablePricePrediction,
      hideAirItineraryReview,
      hideFareDetails,
      hideSeats,
      enableAirShopV4,
      enableLoadingSteps,
      enableIataLookup,
      enableHFv2,
      hideAirPriceFreezeHackerFare,
      hideCfarHackerFare,
      hideChfarHackerFare,
      hideDisruptionHackerFare,
      enableFlightCardUiUpdate,
      enableFintechPurchaseErrorState,
      dedupeFlightsInList,
      enableHorizontalFareDetailsCard,
      uberTripsCoreMigration,
    ]
  );

  useEffect(() => {
    if (enableDarkMode) {
      handleDarkModeChange(true);
    }
  }, [enableDarkMode, handleDarkModeChange]);

  // Adding DD RUM tracking for non-prod environments ONLY
  useEffect(() => {
    if (config.ENV !== "production") {
      const promise = document?.hasStorageAccess?.();
      promise?.then(
        function (hasAccess) {
          logger().info(`hasStorageAccess - success: ${hasAccess}`);
        },
        function (reason) {
          logger().info(`hasStorageAccess - failed: ${reason}`);
        }
      );
      if (!promise) {
        logger().info("hasStorageAccess does not exist on document");
      }
    }
  }, []);

  const flightAppClientAssets = useMemo(
    () => ({
      logo: ClientLogo,
      sessionInfo: sessionInfo,
      featureFlag: {
        enableUberFlightSort: true,
      } as FlightModuleFeatureFlag,
      assets:
        enableDarkMode && getDarkModePreferred()
          ? darkModeIcons
          : lightModeIcons,
      slots: Object.keys(slots),
      isDarkModePreferred,
    }),
    [sessionInfo, enableDarkMode, isDarkModePreferred]
  );

  const tripsAppClientAssets = useMemo(
    () => ({
      sessionInfo: sessionInfo,
      featureFlag: {},
      assets:
        enableDarkMode && getDarkModePreferred()
          ? darkModeIcons
          : lightModeIcons,
    }),
    [sessionInfo, enableDarkMode]
  );

  const renderPathFlightsRoute = useCallback(
    (browserRouterProps: RouteComponentProps) => {
      return (
        <SlotProvider slots={slots}>
          {matchesMobile &&
          browserRouterProps.history.location.pathname === PATH_FLIGHTS ? (
            <MobileLandingPage type={LandingPageType.FLIGHTS} />
          ) : (
            <FlightsApp
              checkoutProps={{ ...flightCheckoutProps, sessionInfo }}
              priceFreezePurchaseProps={{
                ...priceFreezePurchaseProps,
                sessionInfo,
              }}
              apiConfig={apiConfig}
              clientAssets={flightAppClientAssets}
              featureFlags={featureFlags}
              colors={colors}
              theme={theme}
              displayExternalLinksAsModal={false}
              {...browserRouterProps}
            />
          )}
        </SlotProvider>
      );
    },
    [featureFlags, flightAppClientAssets, matchesMobile, sessionInfo, theme]
  );

  const renderPathTripRoute = useCallback(
    (browserRouterProps: RouteComponentProps) => {
      setHeader({ title: getHeaderTitle });

      return (
        <SlotProvider slots={tripSlots}>
          <TripsApp
            apiConfig={apiConfig}
            clientAssets={tripsAppClientAssets}
            featureFlags={featureFlags}
            colors={colors}
            theme={theme}
            onSupportClick={() => generalSupportChat()}
            {...browserRouterProps}
          />
        </SlotProvider>
      );
    },
    [
      featureFlags,
      generalSupportChat,
      getHeaderTitle,
      setHeader,
      theme,
      tripsAppClientAssets,
    ]
  );

  const FlightsRebookWrapper = () => {
    return (
      <FeatureFlagsContext.Provider value={featureFlags ?? {}}>
        <Suspense fallback={<LazyLoading />}>
          <UberFlightsRebookApp />
        </Suspense>
      </FeatureFlagsContext.Provider>
    );
  };

  return (
    <main className={clsx("main-body", classes.main, className)}>
      <Grid className={classes.content}>
        <Switch>
          <CompatRoute path="/flights/rebook">
            <FlightsRebookWrapper />
            <NavigateGlobalHelper />
          </CompatRoute>
          <Route
            path={PATH_FLIGHTS}
            render={isInMaintenance ? Maintenance : renderPathFlightsRoute}
          />
          {uberTripsCoreMigration ? (
            <CompatRoute path={PATH_TRIPS}>
              <FeatureFlagsContext.Provider value={featureFlags ?? {}}>
                <Suspense fallback={<LazyLoading />}>
                  <CoreTripsApp />
                </Suspense>
              </FeatureFlagsContext.Provider>
              <NavigateGlobalHelper />
            </CompatRoute>
          ) : (
            <Route path={PATH_TRIPS} render={renderPathTripRoute} />
          )}
          <Route path="*">
            {/* TODO: once we have started working on the new mobile search workflow changes, this component will likely be relocated
            so that RewardsBanner can be added to the moible home page; see https://hopper-jira.atlassian.net/browse/BP-725 */}
            {isInMaintenance ? (
              <Maintenance />
            ) : matchesMobile ? (
              <MobileLandingPage type={LandingPageType.FLIGHTS} />
            ) : (
              <Redirect to={PATH_FLIGHTS_SEARCH} />
            )}
          </Route>
        </Switch>
      </Grid>
    </main>
  );
};

const NavigateGlobalHelper = () => {
  useInitializeNavigate();
  return null;
};

export default Body;
